import React, {useState, useEffect} from "react";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import MuiAlert from "@mui/material/Alert";
import {Home, Wallet, List, Bell, Search, Menu, User} from "lucide-react";
import FeaturedInfo from "../../components/featuredInfo/FeaturedInfo";
import Arrears from "../../components/arrears/Arrears";
import Transactions from "../../components/transactions/Transactions";
import {loginState, ApiDetails, KEY} from "../../dummyData";
import "./home.css";
import FeaturedInfoModern from "../../components/featuredInfo/FeaturedInfoModern";
import ArrearsModern from "../../components/arrears/ArrearsModern";
import TransactionsModern from "../../components/transactions/TransactionsModern";
import {useNavigate} from "react-router-dom";
import CryptoJS from "crypto-js";
import {Box, Button, Paper, Typography} from "@mui/material";
import {FileText, CreditCard, AlertTriangle} from "lucide-react";
import {useFeatureFlags} from "../../context/FeatureFlagsContext";
import DisabledFeatureBanner from "../../components/FeatureGate/DisabledFeatureBanner";

export default function HomeModern(props) {
    const navigate = useNavigate();
    const { isEnabled, getMessage } = useFeatureFlags();

    // States
    const [backDrop, setBackDrop] = useState(<></>);
    const [updateSet] = useState(false);
    const [open, setOpen] = useState(loginState);
    const [message] = useState("");
    const [severity] = useState("success");
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [activeTab, setActiveTab] = useState("dashboard");

    // Notification states
    const [pendingIdUpload, setPendingIdUpload] = useState(false);
    const [pendingContract, setPendingContract] = useState(false);
    const [unsignedAgreementCount, setUnsignedAgreementCount] = useState(0);

    // Check for pending notifications on mount
    useEffect(() => {
        checkPendingNotifications();
    }, []);

    const checkPendingNotifications = async () => {
        try {
            // Get tenant details from localStorage
            const encryptedDetails = localStorage.getItem("tenant_details");
            if (!encryptedDetails) return;

            const details = CryptoJS.AES.decrypt(encryptedDetails, KEY);
            const originalText = details.toString(CryptoJS.enc.Utf8);
            const tenantDetails = JSON.parse(originalText);

            // Check ID verification status
            const idStatus = tenantDetails.id_verification_status;
            if (idStatus && idStatus.toString().trim().toUpperCase() === "PENDING") {
                setPendingIdUpload(true);
            }

            // Check for unsigned agreements
            const response = await fetch(ApiDetails + "pegasus/visionary/agreements/fetch", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": tenantDetails.access_token
                },
                body: JSON.stringify({
                    tenant_id: tenantDetails.tenant_id,
                    property_id: tenantDetails.property_id,
                    landlord_id: tenantDetails.landlord_id
                }),
            });

            if (response.status === 200 || response.status === 201) {
                const data = await response.json();
                const agreements = Array.isArray(data) ? data : (data.agreements || []);
                // Count agreements where landlord has signed but tenant hasn't
                const unsignedCount = agreements.filter(a =>
                    a.landlord_signed && !a.tenant_signed
                ).length;

                if (unsignedCount > 0) {
                    setPendingContract(true);
                    setUnsignedAgreementCount(unsignedCount);
                }
            }
        } catch (error) {
            console.error("Error checking notifications:", error);
        }
    };

    // Handle snackbar close
    const handleClose = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setOpen(false);
    };

    // Alert component
    const Alert = React.forwardRef(function Alert(props, ref) {
        return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
    });

    // Toggle sidebar
    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    if (!isEnabled('dashboard')) {
        return <DisabledFeatureBanner featureName="Dashboard" message={getMessage('dashboard')} />;
    }

    return (
        <div className="dashboard-container" style={{
            display: 'flex',
            minHeight: '100vh',
            backgroundColor: '#f9fafb'
        }}>
            {/* Main content - Fixed to account for sidebar */}
            <div
                className="main-content"
                style={{
                    flex: 1,
                    // padding: '20px',
                    // No margin needed since Sidebar component handles the layout
                }}
            >
                {/* Page content */}
                <div className="page-content">
                    {/* Snackbar */}
                    {backDrop}
                    <Stack spacing={2} sx={{width: "100%"}}>
                        <Snackbar open={open} autoHideDuration={2000} onClose={handleClose}>
                            <Alert onClose={handleClose} severity={severity} sx={{width: "100%"}}>
                                {message}
                            </Alert>
                        </Snackbar>
                    </Stack>

                    {/* Notification Banners */}
                    {(pendingContract || pendingIdUpload) && (isEnabled('agreements') || isEnabled('profile_id_verification')) && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                            margin: '10px',
                            marginBottom: '20px'
                        }}>
                            {/* Pending Contract Banner */}
                            {pendingContract && isEnabled('agreements') && (
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        borderRadius: '12px',
                                        border: '1px solid #ff9800',
                                        backgroundColor: 'rgba(255, 152, 0, 0.08)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexWrap: 'wrap',
                                        gap: 2
                                    }}
                                >
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                                        <Box sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: '50%',
                                            backgroundColor: 'rgba(255, 152, 0, 0.2)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <FileText size={20} color="#ff9800"/>
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle1" sx={{fontWeight: 600, color: '#e65100'}}>
                                                Pending Agreement{unsignedAgreementCount > 1 ? 's' : ''}
                                            </Typography>
                                            <Typography variant="body2" sx={{color: '#f57c00'}}>
                                                You have {unsignedAgreementCount} tenancy agreement{unsignedAgreementCount > 1 ? 's' : ''} waiting for your signature.
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => navigate('/agreements')}
                                        sx={{
                                            backgroundColor: '#ff9800',
                                            textTransform: 'none',
                                            borderRadius: '8px',
                                            fontWeight: 500,
                                            '&:hover': {backgroundColor: '#f57c00'}
                                        }}
                                    >
                                        Sign Now
                                    </Button>
                                </Paper>
                            )}

                            {/* Pending ID Upload Banner */}
                            {pendingIdUpload && isEnabled('profile_id_verification') && (
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        borderRadius: '12px',
                                        border: '1px solid #2196f3',
                                        backgroundColor: 'rgba(33, 150, 243, 0.08)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexWrap: 'wrap',
                                        gap: 2
                                    }}
                                >
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                                        <Box sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: '50%',
                                            backgroundColor: 'rgba(33, 150, 243, 0.2)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <CreditCard size={20} color="#2196f3"/>
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle1" sx={{fontWeight: 600, color: '#1565c0'}}>
                                                ID Verification Required
                                            </Typography>
                                            <Typography variant="body2" sx={{color: '#1976d2'}}>
                                                Please upload your National ID photos to complete your profile verification.
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => navigate('/profile', {state: {tab: 3}})}
                                        sx={{
                                            backgroundColor: '#2196f3',
                                            textTransform: 'none',
                                            borderRadius: '8px',
                                            fontWeight: 500,
                                            '&:hover': {backgroundColor: '#1976d2'}
                                        }}
                                    >
                                        Upload ID
                                    </Button>
                                </Paper>
                            )}
                        </Box>
                    )}

                    {/* Dashboard widgets */}
                    <div className="dashboard-widgets" style={{
                        display: 'flex',
                        gap: '1.5rem',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                        flexDirection: 'column'
                    }}>
                        <div className="widget featured-info-widget" style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            padding: '1.5rem',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                            border: '1px solid #e5e7eb',
                            margin: '10px'
                        }}>
                            <h2 className="widget-title" style={{
                                fontSize: '1.125rem',
                                fontWeight: '600',
                                color: '#374151',
                                marginBottom: '1rem',
                                marginTop: 0
                            }}>
                                Account Summary
                            </h2>
                            <FeaturedInfoModern snackBar={props.snackBar}/>
                        </div>

                        <div className="widget arrears-widget" style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            padding: '1.5rem',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                            border: '1px solid #e5e7eb',
                            margin: '10px'
                        }}>
                            <h2 className="widget-title" style={{
                                fontSize: '1.125rem',
                                fontWeight: '600',
                                color: '#374151',
                                marginBottom: '1rem',
                                marginTop: 0
                            }}>
                                Arrears
                            </h2>
                            <ArrearsModern snackBar={props.snackBar}/>
                        </div>

                        <div className="widget transactions-widget" style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            padding: '1.5rem',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                            border: '1px solid #e5e7eb',
                            gridColumn: '1 / -1', // Full width for transactions
                            margin: '10px'
                        }}>
                            <h2 className="widget-title" style={{
                                fontSize: '1.125rem',
                                fontWeight: '600',
                                color: '#374151',
                                marginBottom: '1rem',
                                marginTop: 0
                            }}>
                                Recent Transactions
                            </h2>
                            <TransactionsModern snackBar={props.snackBar}/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}